import Layout from "../../../Components/Layout/Layout";
import { useState } from "react";
import axios, { AxiosRequestConfig } from "axios";
import FluentGrid from "../../../Components/FluentGrid";
import { AssessmentsKpis, AssessmentsKpisQuery, CapacityKpisGroup, CompletedAssessmentKpis, InterruptedAssessmentKpis, InterruptedAssessmentKpisQuery, UntouchedAssessmentKpis, UserRole, WorkItemType } from "../../../system/Domain";
import { CircularProgress } from "@mui/material";
import useAxiosEffect from "../../../system/useAxiosEffect";
import { axiosDownload } from "../../../system/axiosDownload";
import { setWorkSession } from "../../../system/reducers/workSessions/actions";
import moment from "moment";
import { useDispatch } from "react-redux";
import KpisGroupTile from "../Dashboard/CapacityKpisGroupTile";
import OrdersNavigation from "Navigation/OrdersNavigation";
import { CheckCircle, NoCrash, ReportProblem, Warehouse } from "@mui/icons-material";
import useUser from "../../../system/useUser";

const AssessmentsDashboardLayout = () => {
	const dispatch = useDispatch();
	const [user, , hasRole] = useUser();

	const canStartSession = hasRole(UserRole.AldUser) || hasRole(UserRole.ValidationManager) || hasRole(UserRole.LotManager);
	const isAldUser = hasRole(UserRole.AldUser);
	const canSeeDekra = isAldUser || user?.company?.name === "Dekra";
	const canSeeMacadam = isAldUser || user?.company?.name === "Macadam";

	const [assessmentKpis, setAssessmentKpis] = useState<AssessmentsKpis>();
	const loadAssessmentKpis = async (axiosConfig: AxiosRequestConfig = {}) => {
		const { data } = await axios.get<AssessmentsKpis>(`/api/orders/assessments/kpis`, axiosConfig);
		setAssessmentKpis(data);
	};

	const startAssessmentsKpisKpisSession = async (group: CapacityKpisGroup<AssessmentsKpisQuery>) => {
		if (!canStartSession) return;

		let workItemType: WorkItemType = "AssessmentOrder";
		const { data: workSession } = await axios.post(`/api/orders/assessments/kpis/work-session`, {
			title: group.name,
			workItemType,
			query: group.query
		});

		if (workSession) {
			dispatch(setWorkSession(workSession));
		}
	};

	const exportAssesmentsKpis = async (group: CapacityKpisGroup<AssessmentsKpisQuery>) => {
		const response = await axios.post(`/api/orders/assessments/kpis/export`, group.query, {
			responseType: "blob"
		});

		axiosDownload(response, `${moment().format('YYYY-MM-DD')} ${group.name}.xlsx`);
	};

	const [interruptedAssessmentKpis, setInterruptedAssessmentKpis] = useState<InterruptedAssessmentKpis>();
	const loadInterruptedAssessmentKpis = async (axiosConfig: AxiosRequestConfig = {}) => {
		const { data } = await axios.get<InterruptedAssessmentKpis>(`/api/orders/assessments/interrupted-kpis`, axiosConfig);
		setInterruptedAssessmentKpis(data);
	};

	const startInterruptedAssessmentsKpisSession = async (group: CapacityKpisGroup<InterruptedAssessmentKpisQuery>) => {
		if (!canStartSession) return;

		let workItemType: WorkItemType = "AssessmentOrderDetails";
		const { data: workSession } = await axios.post(`/api/orders/assessments/interrupted-kpis/work-session`, {
			title: group.name,
			workItemType,
			query: group.query
		});

		if (workSession) {
			dispatch(setWorkSession(workSession));
		}
	};

	const exportInterruptedAssessmentsKpis = async (group: CapacityKpisGroup<InterruptedAssessmentKpisQuery>) => {
		const response = await axios.post(`/api/orders/assessments/interrupted-kpis/export`, group.query, {
			responseType: "blob"
		});

		axiosDownload(response, `${moment().format('YYYY-MM-DD')} ${group.name}.xlsx`);
	};

	const [completedAssessmentKpis, setCompletedAssessmentKpis] = useState<CompletedAssessmentKpis>();
	const loadCompletedAssessmentKpis = async (axiosConfig: AxiosRequestConfig = {}) => {
		const { data } = await axios.get<CompletedAssessmentKpis>(`/api/orders/assessments/completed-kpis`, axiosConfig);
		setCompletedAssessmentKpis(data);
	};

	const startCompletedAssessmentsKpisSession = async (group: CapacityKpisGroup<AssessmentsKpisQuery>) => {
		if (!canStartSession) return;

		let workItemType: WorkItemType = "AssessmentOrderDetails";
		const { data: workSession } = await axios.post(`/api/orders/assessments/completed-kpis/work-session`, {
			title: group.name,
			workItemType,
			query: group.query
		});

		if (workSession) {
			dispatch(setWorkSession(workSession));
		}
	};

	const exportCompletedAssessmentsKpis = async (group: CapacityKpisGroup<AssessmentsKpisQuery>) => {
		const response = await axios.post(`/api/orders/assessments/completed-kpis/export`, group.query, {
			responseType: "blob"
		});

		axiosDownload(response, `${moment().format('YYYY-MM-DD')} ${group.name}.xlsx`);
	};

	useAxiosEffect(loadAssessmentKpis, []);
	useAxiosEffect(loadInterruptedAssessmentKpis, []);
	useAxiosEffect(loadCompletedAssessmentKpis, []);

	const [untouchedAssessmentKpis, setUntouchedAssessmentKpis] = useState<UntouchedAssessmentKpis>();
	const loadUntouchedAssessmentKpis = async (axiosConfig: AxiosRequestConfig = {}) => {
		const { data } = await axios.get<UntouchedAssessmentKpis>(`/api/orders/assessments/untouched-kpis`, axiosConfig);
		setUntouchedAssessmentKpis(data);
	};
	useAxiosEffect(loadUntouchedAssessmentKpis, []);

	const startUntouchedAssessmentsKpisSession = async (group: CapacityKpisGroup<AssessmentsKpisQuery>) => {
		if (!canStartSession) return;

		let workItemType: WorkItemType = "AssessmentOrderDetails";
		const { data: workSession } = await axios.post(`/api/orders/assessments/untouched-kpis/work-session`, {
			title: group.name,
			workItemType,
			query: group.query
		});

		if (workSession) {
			dispatch(setWorkSession(workSession));
		}
	};

	return (
		<Layout
			title="Gutachten Dashboard"
			navigation={<OrdersNavigation />}
		>
			{(!assessmentKpis || !interruptedAssessmentKpis || !untouchedAssessmentKpis || !completedAssessmentKpis) && (
				<CircularProgress size={48} />
			)}

			{assessmentKpis && interruptedAssessmentKpis && untouchedAssessmentKpis && completedAssessmentKpis && !canSeeDekra && !canSeeMacadam && (
				"Nicht autorisiert"
			)}

			{assessmentKpis && interruptedAssessmentKpis && untouchedAssessmentKpis && completedAssessmentKpis && (canSeeDekra || canSeeMacadam) && (
				<FluentGrid itemWidth={350} itemGap={6}>
					{canSeeDekra && (
						<KpisGroupTile
							tileName={assessmentKpis.dekra.name}
							tileIcon={<NoCrash />}
							group={assessmentKpis.dekra}
							showNullAge
							startSession={startAssessmentsKpisKpisSession}
							exportKpis={exportAssesmentsKpis}
						/>
					)}
					{canSeeMacadam && (
						<KpisGroupTile
							tileName={assessmentKpis.macadam.name}
							tileIcon={<NoCrash />}
							group={assessmentKpis.macadam}
							showNullAge
							startSession={startAssessmentsKpisKpisSession}
							exportKpis={exportAssesmentsKpis}
						/>
					)}
					{canSeeDekra && (
						<KpisGroupTile
							tileName={interruptedAssessmentKpis.dekra.name}
							tileIcon={<ReportProblem />}
							group={interruptedAssessmentKpis.dekra}
							showNullAge
							startSession={startInterruptedAssessmentsKpisSession}
							exportKpis={exportInterruptedAssessmentsKpis}
						/>
					)}
					{canSeeMacadam && (
						<KpisGroupTile
							tileName={interruptedAssessmentKpis.macadam.name}
							tileIcon={<ReportProblem />}
							group={interruptedAssessmentKpis.macadam}
							showNullAge
							startSession={startInterruptedAssessmentsKpisSession}
							exportKpis={exportInterruptedAssessmentsKpis}
						/>
					)}
					{canSeeDekra && (
						<KpisGroupTile
							tileName={untouchedAssessmentKpis.dekra.name}
							tileIcon={<Warehouse />}
							group={untouchedAssessmentKpis.dekra}
							showNullAge
							hideEmptyEntries
							startSession={startUntouchedAssessmentsKpisSession}
							exportKpis={exportAssesmentsKpis}
						/>
					)}
					{canSeeMacadam && (
						<KpisGroupTile
							tileName={untouchedAssessmentKpis.macadam.name}
							tileIcon={<Warehouse />}
							group={untouchedAssessmentKpis.macadam}
							showNullAge
							hideEmptyEntries
							startSession={startUntouchedAssessmentsKpisSession}
							exportKpis={exportAssesmentsKpis}
						/>
					)}
					{canSeeDekra && (
						<KpisGroupTile
							tileName={completedAssessmentKpis.dekra.name}
							tileIcon={<CheckCircle />}
							group={completedAssessmentKpis.dekra}
							hideEmptyEntries
							startSession={startCompletedAssessmentsKpisSession}
							exportKpis={exportCompletedAssessmentsKpis}
						/>
					)}
					{canSeeMacadam && (
						<KpisGroupTile
							tileName={completedAssessmentKpis.macadam.name}
							tileIcon={<CheckCircle />}
							group={completedAssessmentKpis.macadam}
							hideEmptyEntries
							startSession={startCompletedAssessmentsKpisSession}
							exportKpis={exportCompletedAssessmentsKpis}
						/>
					)}
				</FluentGrid>
			)}
		</Layout>
	);
};

export default AssessmentsDashboardLayout;
